import React from 'react';
import { SemanticUI } from '@iterable/deprecated_react_components';
import { BUTTON_SIZES } from 'ui/button';
import { isFunction } from 'utils/helpers';
import { noop } from 'utils/noop';
import {
  ActionCellContainer,
  ActionButtonsContainer,
  StyledGhostButton,
} from './ActionCell.styled';

// type Props = {
//   /** Additional classes. */
//   className: string,
//   /** Additional content to show. */
//   contents: node,
//   /** Icon name for custom button. */
//   customIcon: string,
//   /** If actions are disabled. */
//   disabled: boolean,
//   /** Function to get contents of the ActionCell. Overrides `contents` prop. */
//   getContents: ?(rowData: {}) => node,
//   /** Hide the action buttons. */
//   hideActions: boolean,
//   /** If passed in, will display a custom button and run this function when clicked. */
//   onClickCustom: ?(e: {}, rowData: {}) => {},
//   /** If passed in, will display an edit button and run this function when clicked. */
//   onClickEdit: ?(e: {}, rowData: {}) => {},
//   /** If passed in, will display a delete button and run this function when clicked. */
//   onClickDelete: ?(e: {}, rowData: {}) => {},
//   /** Data of the row. */
//   rowData: {},
//   /** Width of the action cell. If not passed it, width will be automatically calculated based on number of actions. */
//   width: number,
// };

export default class ActionCell extends React.Component {
  static defaultProps = {
    customIcon: 'ellipsis horizontal',
    getContents: noop,
  };

  /**
   * canEdit
   * @returns { Boolean } If edit is allowed.
   */
  canCustom = () => isFunction(this.props.onClickCustom);

  /**
   * canDelete
   * @returns { Boolean } If delete is allowed.
   */
  canDelete = () => isFunction(this.props.onClickDelete);

  /**
   * canEdit
   * @returns { Boolean } If edit is allowed.
   */
  canEdit = () => isFunction(this.props.onClickEdit);

  /**
   * handleCustom
   * Callback function when custom button is clicked.
   * @param { Object } e: The click event.
   * @returns { void }
   */
  handleCustom = (e) => {
    const { onClickCustom, rowData } = this.props;
    onClickCustom(e, rowData);
  };

  /**
   * handleDelete
   * Callback function when delete button is clicked.
   * @param { Object } e: The click event.
   * @returns { void }
   */
  handleDelete = (e) => {
    const { onClickDelete, rowData } = this.props;
    onClickDelete(e, rowData);
  };

  /**
   * handleEdit
   * Callback function when edit button is clicked.
   * @param { Object } e: The click event.
   * @returns { void }
   */
  handleEdit = (e) => {
    const { onClickEdit, rowData } = this.props;
    onClickEdit(e, rowData);
  };

  /**
   * getCellContents
   * Gets the contents of the action cell.
   * @returns { React.Component } The cell contents.
   */
  getCellContents = () => {
    const { contents, getContents, rowData } = this.props;
    return getContents(rowData) || contents;
  };

  /**
   * renderActionButtons
   * Renders the action buttons.
   * @returns { React.Component } The action buttons.
   */
  renderActionButtons = () => {
    const { customIcon, disabled } = this.props;
    return (
      <ActionButtonsContainer>
        {this.canEdit() && (
          <StyledGhostButton
            size={BUTTON_SIZES.SMALL}
            appearance="pill"
            data-test="editButton"
            disabled={disabled}
            onClick={this.handleEdit}
          >
            <SemanticUI.Icon name="edit" />
          </StyledGhostButton>
        )}
        {this.canDelete() && (
          <StyledGhostButton
            size={BUTTON_SIZES.SMALL}
            data-test="trashButton"
            appearance="pill"
            disabled={disabled}
            onClick={this.handleDelete}
          >
            <SemanticUI.Icon name="trash alternate outline" />
          </StyledGhostButton>
        )}
        {this.canCustom() && (
          <StyledGhostButton
            size={BUTTON_SIZES.SMALL}
            appearance="pill"
            disabled={disabled}
            onClick={this.handleCustom}
          >
            <SemanticUI.Icon name={customIcon} />
          </StyledGhostButton>
        )}
      </ActionButtonsContainer>
    );
  };

  /**
   * getNumberOfActions
   * Calculates the number of action buttons rendered.
   * @returns { Number } The number of action buttons.
   */
  getNumberOfActions = () => {
    let numberOfActions = 0;
    if (this.canCustom()) {
      numberOfActions += 1;
    }
    if (this.canEdit()) {
      numberOfActions += 1;
    }
    if (this.canDelete()) {
      numberOfActions += 1;
    }
    return numberOfActions;
  };

  render() {
    const { className, hideActions, width } = this.props;
    const style = width ? { minWidth: width, maxWidth: width } : undefined;

    return (
      <ActionCellContainer
        className={className}
        data-number-of-actions={width ? undefined : this.getNumberOfActions()}
        data-role="actions"
        style={style}
      >
        {this.getCellContents()}
        {!hideActions && this.renderActionButtons()}
      </ActionCellContainer>
    );
  }
}
